import { logger } from '@flexible-reports/services/logger';
import {
  AccountId,
  FlexibleSubscription,
  ReportConfiguration,
  ReportConfigurationSchedule,
  ReportPeriod
} from '@flexible-reports/store/api/subscriptions/types';
import { zonedTimeToUtc } from 'date-fns-tz';
import { shouldExcludeManagerAccounts } from '@flexible-reports/utils/app-utils';

export const transformManagerAccounts = (accounts: AccountId[]): string[] => {
  if (shouldExcludeManagerAccounts()) {
    return [];
  }
  const managerAccounts: string[] = [];
  accounts.forEach((account: AccountId) => {
    if (account.managerAccountId) {
      managerAccounts.push(account.managerAccountId);
    }
  });
  return managerAccounts;
};

export const transformAdvertiserAccounts = (accounts: AccountId[]): string[] => {
  const advertiserAccounts: string[] = [];
  accounts.forEach((account: AccountId) => {
    if (account.advertiserAccountId) {
      advertiserAccounts.push(account.advertiserAccountId);
    }
  });
  return advertiserAccounts;
};

export const transformReportPeriod = (reportPeriod: ReportPeriod) => {
  return { datePeriod: reportPeriod.datePeriod, preset: reportPeriod.reportConfigurationPresetPeriod?.preset };
};

export const transformScheduleEndDate = (schedule: ReportConfigurationSchedule) => {
  const scheduleType = schedule.recurringDailySchedule || schedule.recurringWeeklySchedule || schedule.recurringMonthlySchedule;

  try {
    if (scheduleType?.expirationDate && scheduleType?.hourOfDay && scheduleType?.timeZone) {
      const date = new Date(`${scheduleType.expirationDate} ${scheduleType.hourOfDay.toString().padStart(2, '0')}:00:00`);
      const timezone = scheduleType.timeZone.replace('_', '/');
      return zonedTimeToUtc(date, timezone).toISOString();
    }
    return null;
  } catch (error) {
    logger.logFatalMessage(
      `Error converting end date to UTC. Date string: ${scheduleType?.expirationDate} ${
        scheduleType?.hourOfDay
      }:00:00, Original Timezone: ${scheduleType?.timeZone}, Parsed Timezone: ${scheduleType?.timeZone?.replace('_', '/')}`,
      error
    );
    return null;
  }
};

export const transformReportConfigurationsToSubscriptions = (reportConfigurations: ReportConfiguration[]): FlexibleSubscription[] => {
  return reportConfigurations.map((reportConfiguration: ReportConfiguration) => {
    return {
      id: reportConfiguration.reportConfigurationId,
      state: reportConfiguration.status,
      name: reportConfiguration.name,
      reportId: reportConfiguration.latestScheduledReportId,
      status: reportConfiguration.latestScheduledReportStatus,
      failureCode: reportConfiguration.latestScheduledReportFailureCode,
      lastRun: reportConfiguration.latestScheduledReportLastUpdatedDateTime,
      nextRun: reportConfiguration.nextScheduledReportDateTime,
      accounts: transformManagerAccounts(reportConfiguration.linkedAccounts),
      advertisers: transformAdvertiserAccounts(reportConfiguration.linkedAccounts),
      fields: reportConfiguration.query.fields,
      frequency: reportConfiguration.scheduleType,
      format: reportConfiguration.format,
      reportPeriod: transformReportPeriod(reportConfiguration.period),
      lastModifiedDate: reportConfiguration.lastUpdatedDateTime,
      createdDate: reportConfiguration.creationDateTime,
      scheduleEndDate: transformScheduleEndDate(reportConfiguration.schedule),
      recipients: reportConfiguration.deliverySettings.email ?? [],
      linkedAccounts: reportConfiguration.linkedAccounts
    };
  });
};
